import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { GbGemeindeListEntry, GbgemeindeService } from 'app/core/gb-api';
import { Subscription } from 'rxjs';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelektionskriterienDetailEntryMeta } from 'app/core/batch-auftrag-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppPermissionService } from 'app/core/services/app-permission.service';
import { languages_name } from 'app/shared/functions/language.functions';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { AbstractTypeaheadSearch } from '../../../../../shared/components/typeahead-search/abstract-typeahead-search';
import { NgbTooltip, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { MetaDisablerDirective } from '../../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../../shared/directives/meta-validator.directive';
import { TranslateNamePipe } from '../../../../../shared/pipes/simple/translate-name.pipe';

@Component({
  selector: 'cap-auftrag-gemeinde-selection',
  templateUrl: './auftrag-gemeinde-selection.component.html',
  styleUrls: ['./auftrag-gemeinde-selection.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AuftragGemeindeSelectionComponent),
      multi: true
    }
  ],
  imports: [FormsModule, NgbTypeahead, MetaDisablerDirective, MetaValidatorDirective, NgbTooltip, TranslatePipe, TranslateNamePipe]
})
export class AuftragGemeindeSelectionComponent
  extends AbstractTypeaheadSearch<GbGemeindeListEntry>
  implements OnInit, OnDestroy, ControlValueAccessor
{
  constructor(
    private translate: TranslateService,
    private gbGemeindeService: GbgemeindeService,
    private permissionService: AppPermissionService
  ) {
    keysToTranslate<string>(['GemeindeEntfernen', 'GemeindeAuswahl', 'Gemeinden']);

    super();
    this.showInputAfterSelect = false;
  }

  @Input()
  nurGueltige = true;

  @Input()
  showKreisnummer = true;

  @Input()
  mitExternen = true;

  @Input()
  mitAufSuchmaskeVerborgenen = false;

  @Input()
  readonly: boolean;

  model: GbGemeindeListEntry;

  hasPermissionSeybuch: boolean;
  selektionskriterien: SelektionskriterienDetailEntryMeta;
  gemeinden: Set<GbGemeindeListEntry> = new Set([]);

  private gemeindeSubscription: Subscription;

  propagateChange: (id: string) => void;
  propagateTouch: (_) => void;

  ngOnInit() {
    this.checkPermissionSeybuch();
    const findAll = this.gbGemeindeService.loadGbGemeindeListEntries(
      'DATENEXPORT_NEW', //
      this.nurGueltige,
      this.mitExternen,
      this.hasPermissionSeybuch,
      this.mitAufSuchmaskeVerborgenen
    );
    this.setSearch(findAll);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  onSelected($event: NgbTypeaheadSelectItemEvent<GbGemeindeListEntry>) {
    const selectedItem: GbGemeindeListEntry = $event.item;
    if (selectedItem) {
      const gemeindenArray = []; // Für IGE kann nur eine Gemeinde ausgewählt werden
      gemeindenArray.unshift(selectedItem);
      this.model = selectedItem;
      this.gemeinden = new Set(gemeindenArray);
      this.selektionskriterien.gemeindeId = gemeindenArray.map((g) => g.id);
    }
    this.onClear();
  }

  formatEntry(entry: GbGemeindeListEntry): string {
    if (entry) {
      const currentLanguageProperty = languages_name[this.translate.currentLang].name;
      let title = entry[currentLanguageProperty];
      if (entry.bfsNummer) {
        title += ' ' + entry.bfsNummer;
      }
      if (this.showKreisnummer && entry.kreisNummer) {
        title += '.' + entry.kreisNummer;
      }
      return title;
    }
    return '';
  }

  writeValue(obj: SelektionskriterienDetailEntryMeta) {
    if (obj) {
      this.gemeinden.clear();
      this.selektionskriterien = obj;
      this.selektionskriterien.gemeindeId.forEach((element) => {
        this.gemeindeSubscription = this.gbGemeindeService.loadGbGemeindeListEntryById(element.id).subscribe((gemeinde) => {
          this.gemeinden.add(gemeinde);
        });
      });
    }
  }

  checkPermissionSeybuch() {
    this.hasPermissionSeybuch = this.permissionService.hasPermission('SEYBUCH_REGISTER');
  }

  ngOnDestroy(): void {
    this.gemeindeSubscription?.unsubscribe();
  }

  onDelete(code: any) {
    this.gemeinden.delete(code);
    const gArray = [];
    this.gemeinden.forEach((gemeinde) => gArray.push(gemeinde));
    this.selektionskriterien.gemeindeId = gArray.map((g) => g.id);
  }
}
